import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell, TableHead,
  TableRow, Button, Alert, IconButton, Chip, Tooltip, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, MenuItem, Grid, Divider,
} from '@mui/material';
import { Add, Edit, Home, Event, Campaign, Grading } from '@mui/icons-material';
import axios from 'axios';
import { useUnsavedGuard } from '../hooks/useUnsavedGuard';
import { isRealDate } from '../utils/dateValidation';

const API = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const fmtDate = (d) => d ? new Date(d).toLocaleDateString() : '—';
const STATUS_COLOR = { Planning: 'default', Active: 'success', Evaluation: 'warning', Closed: 'error' };

const emptyPeriod = { code: '', name: '', financialYear: '', startDate: '', endDate: '', midReviewDate: '', status: 'Planning' };

const AppraisalSetup = () => {
  const navigate = useNavigate();
  const [periods, setPeriods] = useState([]);
  const [scale, setScale]     = useState([]);
  const [error, setError]     = useState('');
  const [msg, setMsg]         = useState('');
  const [pDlg, setPDlg]       = useState(null);   // period form
  const [sDlg, setSDlg]       = useState(null);   // scale band form
  const pGuard = useUnsavedGuard({ open: Boolean(pDlg), value: pDlg, onClose: () => setPDlg(null) });
  const sGuard = useUnsavedGuard({ open: Boolean(sDlg), value: sDlg, onClose: () => setSDlg(null) });

  const load = useCallback(async () => {
    try {
      const [p, s] = await Promise.all([
        axios.get(`${API}/api/appraisal/periods`, { headers: hdrs() }),
        axios.get(`${API}/api/appraisal/rating-scale`, { headers: hdrs() }),
      ]);
      setPeriods(p.data); setScale(s.data);
    } catch (e) { setError(e.response?.data?.error || 'Failed to load setup'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(''), 3000); };

  const savePeriod = async () => {
    try {
      if (pDlg._id) await axios.put(`${API}/api/appraisal/periods/${pDlg._id}`, pDlg, { headers: hdrs() });
      else          await axios.post(`${API}/api/appraisal/periods`, pDlg, { headers: hdrs() });
      setPDlg(null); load(); flash('Period saved');
    } catch (e) { setError(e.response?.data?.error || 'Save failed'); }
  };

  const notify = async (p) => {
    try { await axios.post(`${API}/api/appraisal/periods/${p._id}/notify`, {}, { headers: hdrs() }); load(); flash('Self-evaluation opened — staff can now set goals'); }
    catch (e) { setError(e.response?.data?.error || 'Failed'); }
  };

  const saveBand = async () => {
    try {
      const body = { ...sDlg, minPercent: Number(sDlg.minPercent), maxPercent: Number(sDlg.maxPercent) };
      if (sDlg._id) await axios.put(`${API}/api/appraisal/rating-scale/${sDlg._id}`, body, { headers: hdrs() });
      else          await axios.post(`${API}/api/appraisal/rating-scale`, body, { headers: hdrs() });
      setSDlg(null); load(); flash('Rating band saved');
    } catch (e) { setError(e.response?.data?.error || 'Save failed'); }
  };

  return (
    <Box sx={{ maxWidth: 1050, mx: 'auto', mt: 4, mb: 4, px: 2 }}>
      <Card elevation={3}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
            <Box display="flex" alignItems="center" gap={1}>
              <Event color="primary" />
              <Typography variant="h5" color="primary">Appraisal Setup</Typography>
            </Box>
            <Box display="flex" gap={1}>
              <Tooltip title="Appraisals"><IconButton onClick={() => navigate('/hr/appraisal')}><Home /></IconButton></Tooltip>
            </Box>
          </Box>

          {msg   && <Alert severity="success" sx={{ mb: 2 }}>{msg}</Alert>}
          {error && <Alert severity="error"   sx={{ mb: 2 }}>{error}</Alert>}

          {/* Periods */}
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
            <Typography variant="subtitle1" fontWeight={700}>Appraisal Periods</Typography>
            <Button size="small" variant="contained" startIcon={<Add />} onClick={() => setPDlg({ ...emptyPeriod })}>New Period</Button>
          </Box>
          <Table size="small">
            <TableHead><TableRow sx={{ bgcolor: 'grey.50' }}>
              <TableCell><strong>Code</strong></TableCell><TableCell><strong>Name</strong></TableCell>
              <TableCell><strong>Start</strong></TableCell><TableCell><strong>End</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell><TableCell><strong>Self-Eval</strong></TableCell>
              <TableCell align="right"><strong>Actions</strong></TableCell>
            </TableRow></TableHead>
            <TableBody>
              {periods.length === 0 ? (
                <TableRow><TableCell colSpan={7} align="center"><Typography variant="body2" color="text.secondary" py={2}>No periods yet.</Typography></TableCell></TableRow>
              ) : periods.map(p => (
                <TableRow key={p._id} hover>
                  <TableCell><strong>{p.code}</strong></TableCell>
                  <TableCell>{p.name}</TableCell>
                  <TableCell>{fmtDate(p.startDate)}</TableCell>
                  <TableCell>{fmtDate(p.endDate)}</TableCell>
                  <TableCell><Chip label={p.status} size="small" color={STATUS_COLOR[p.status] || 'default'} /></TableCell>
                  <TableCell>{p.selfEvalOpen ? <Chip label="Open" size="small" color="success" variant="outlined" /> : <Chip label="Closed" size="small" variant="outlined" />}</TableCell>
                  <TableCell align="right">
                    {!p.selfEvalOpen && (
                      <Tooltip title="Open self-evaluation & notify staff"><IconButton size="small" color="primary" onClick={() => notify(p)}><Campaign fontSize="small" /></IconButton></Tooltip>
                    )}
                    <Tooltip title="Edit"><IconButton size="small" onClick={() => setPDlg({ ...p, startDate: p.startDate?.slice(0,10) || '', endDate: p.endDate?.slice(0,10) || '', midReviewDate: p.midReviewDate?.slice(0,10) || '' })}><Edit fontSize="small" /></IconButton></Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <Divider sx={{ my: 3 }} />

          {/* Rating scale */}
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
            <Box display="flex" alignItems="center" gap={1}>
              <Grading color="primary" fontSize="small" />
              <Typography variant="subtitle1" fontWeight={700}>Rating Scale</Typography>
            </Box>
            <Button size="small" variant="outlined" startIcon={<Add />} onClick={() => setSDlg({ code: '', label: '', minPercent: '', maxPercent: '', meaning: '', recommendation: '', isPIP: false })}>Add Band</Button>
          </Box>
          <Table size="small">
            <TableHead><TableRow sx={{ bgcolor: 'grey.50' }}>
              <TableCell><strong>Band</strong></TableCell><TableCell><strong>Label</strong></TableCell>
              <TableCell><strong>Range %</strong></TableCell><TableCell><strong>Meaning</strong></TableCell>
              <TableCell><strong>Recommendation</strong></TableCell><TableCell align="right"></TableCell>
            </TableRow></TableHead>
            <TableBody>
              {scale.map(b => (
                <TableRow key={b._id} hover>
                  <TableCell><Chip label={b.code} size="small" sx={{ bgcolor: (b.color || '#1565c0') + '22', color: b.color || '#1565c0', fontWeight: 700 }} /></TableCell>
                  <TableCell>{b.label}{b.isPIP && <Chip label="PIP" size="small" color="error" variant="outlined" sx={{ ml: 1, height: 18 }} />}</TableCell>
                  <TableCell>{b.minPercent}–{b.maxPercent}</TableCell>
                  <TableCell><Typography variant="caption">{b.meaning}</Typography></TableCell>
                  <TableCell><Typography variant="caption">{b.recommendation}</Typography></TableCell>
                  <TableCell align="right"><IconButton size="small" onClick={() => setSDlg(b)}><Edit fontSize="small" /></IconButton></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Period dialog */}
      {pGuard.discardDialog}
      <Dialog open={Boolean(pDlg)} onClose={pGuard.attemptClose} maxWidth="sm" fullWidth>
        <DialogTitle>{pDlg?._id ? `Edit Period — ${pDlg.code}` : 'New Appraisal Period'}</DialogTitle>
        <DialogContent dividers>
          {pDlg && (
            <Grid container spacing={2}>
              <Grid item xs={6}><TextField label="Code *" value={pDlg.code} onChange={e => setPDlg({ ...pDlg, code: e.target.value.toUpperCase() })} fullWidth size="small" placeholder="FY2026" /></Grid>
              <Grid item xs={6}><TextField label="Financial Year" value={pDlg.financialYear} onChange={e => setPDlg({ ...pDlg, financialYear: e.target.value })} fullWidth size="small" /></Grid>
              <Grid item xs={12}><TextField label="Name *" value={pDlg.name} onChange={e => setPDlg({ ...pDlg, name: e.target.value })} fullWidth size="small" placeholder="Annual Appraisal 2026" /></Grid>
              <Grid item xs={6}><TextField type="date" label="Start *" InputLabelProps={{ shrink: true }} value={pDlg.startDate} onChange={e => setPDlg({ ...pDlg, startDate: e.target.value })} fullWidth size="small"
                error={!!(pDlg.startDate && !isRealDate(pDlg.startDate))}
                helperText={pDlg.startDate && !isRealDate(pDlg.startDate) ? 'Not a valid date' : ' '} /></Grid>
              <Grid item xs={6}><TextField type="date" label="End *" InputLabelProps={{ shrink: true }} value={pDlg.endDate} onChange={e => setPDlg({ ...pDlg, endDate: e.target.value })} fullWidth size="small"
                inputProps={{ min: pDlg.startDate || undefined }}
                error={!!((pDlg.endDate && !isRealDate(pDlg.endDate)) || (pDlg.startDate && pDlg.endDate && pDlg.endDate < pDlg.startDate))}
                helperText={(pDlg.endDate && !isRealDate(pDlg.endDate)) ? 'Not a valid date' : (pDlg.startDate && pDlg.endDate && pDlg.endDate < pDlg.startDate) ? 'End must be on/after start' : ' '} /></Grid>
              <Grid item xs={6}><TextField type="date" label="Mid-Review Date" InputLabelProps={{ shrink: true }} value={pDlg.midReviewDate} onChange={e => setPDlg({ ...pDlg, midReviewDate: e.target.value })} fullWidth size="small"
                inputProps={{ min: pDlg.startDate || undefined, max: pDlg.endDate || undefined }} /></Grid>
              <Grid item xs={6}><TextField select label="Status" value={pDlg.status} onChange={e => setPDlg({ ...pDlg, status: e.target.value })} fullWidth size="small">
                {['Planning', 'Active', 'Evaluation', 'Closed'].map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </TextField></Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions><Button onClick={pGuard.attemptClose}>Cancel</Button>
          <Button variant="contained" onClick={savePeriod}
            disabled={!!((pDlg?.startDate && !isRealDate(pDlg.startDate)) || (pDlg?.endDate && !isRealDate(pDlg.endDate)) || (pDlg?.startDate && pDlg?.endDate && pDlg.endDate < pDlg.startDate))}>Save</Button></DialogActions>
      </Dialog>

      {/* Rating band dialog */}
      {sGuard.discardDialog}
      <Dialog open={Boolean(sDlg)} onClose={sGuard.attemptClose} maxWidth="sm" fullWidth>
        <DialogTitle>{sDlg?._id ? `Edit Band — ${sDlg.code}` : 'New Rating Band'}</DialogTitle>
        <DialogContent dividers>
          {sDlg && (
            <Grid container spacing={2}>
              <Grid item xs={4}><TextField label="Code *" value={sDlg.code} onChange={e => setSDlg({ ...sDlg, code: e.target.value })} fullWidth size="small" placeholder="5" /></Grid>
              <Grid item xs={8}><TextField label="Label *" value={sDlg.label} onChange={e => setSDlg({ ...sDlg, label: e.target.value })} fullWidth size="small" placeholder="Excellent" /></Grid>
              <Grid item xs={6}><TextField type="number" label="Min %" value={sDlg.minPercent} onChange={e => setSDlg({ ...sDlg, minPercent: e.target.value })} fullWidth size="small" /></Grid>
              <Grid item xs={6}><TextField type="number" label="Max %" value={sDlg.maxPercent} onChange={e => setSDlg({ ...sDlg, maxPercent: e.target.value })} fullWidth size="small" /></Grid>
              <Grid item xs={12}><TextField label="Meaning" value={sDlg.meaning} onChange={e => setSDlg({ ...sDlg, meaning: e.target.value })} fullWidth size="small" /></Grid>
              <Grid item xs={12}><TextField label="Recommendation" value={sDlg.recommendation} onChange={e => setSDlg({ ...sDlg, recommendation: e.target.value })} fullWidth size="small" /></Grid>
              <Grid item xs={12}><TextField select label="Triggers PIP?" value={sDlg.isPIP ? 'yes' : 'no'} onChange={e => setSDlg({ ...sDlg, isPIP: e.target.value === 'yes' })} fullWidth size="small">
                <MenuItem value="no">No</MenuItem><MenuItem value="yes">Yes — poor band, place on PIP</MenuItem>
              </TextField></Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions><Button onClick={sGuard.attemptClose}>Cancel</Button><Button variant="contained" onClick={saveBand}>Save</Button></DialogActions>
      </Dialog>
    </Box>
  );
};

export default AppraisalSetup;
